import { registerFeatureBootstrap } from "../bootstrap";
import { closestFromEvent } from "../utils/dom";
import {
  defaultHubRuntimeEnv,
  postHubForm,
  readHubErrorMessage,
  setHubFeedback,
  type HubRuntimeEnv,
} from "./hub_actions";

const LIBRARY_HUB_SELECTOR = "[data-library-hub-root]";

const cleanupByRoot = new WeakMap<HTMLElement, () => void>();

function nodeWithinRoot(root: HTMLElement, node: HTMLElement | null): HTMLElement | null {
  return node instanceof HTMLElement && root.contains(node) ? node : null;
}

function feedbackNode(root: HTMLElement): HTMLElement | null {
  const node = root.querySelector("[data-library-feedback]");
  return node instanceof HTMLElement ? node : null;
}

function showLibraryFeedback(root: HTMLElement, message: string, isError: boolean): void {
  setHubFeedback(feedbackNode(root), message, isError);
}

function pendingText(button: HTMLButtonElement): string {
  return String(button.getAttribute("data-library-pending-text") || "").trim() || "Sending...";
}

function pendingMessage(button: HTMLButtonElement): string {
  return String(button.getAttribute("data-library-pending-message") || "").trim() || "Sending request...";
}

function setPendingButtonState(button: HTMLButtonElement): void {
  if (!button.dataset.originalText) {
    button.dataset.originalText = button.textContent || "";
  }
  button.disabled = true;
  button.classList.add("is-loading", "btn-loading");
  button.textContent = pendingText(button);
}

function clearPendingButtonState(button: HTMLButtonElement): void {
  button.disabled = false;
  button.classList.remove("is-loading", "btn-loading");
  if (button.dataset.originalText) {
    button.textContent = button.dataset.originalText;
  }
}

function librarySpines(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll("[data-book-spine]")).filter((node): node is HTMLElement => node instanceof HTMLElement);
}

function libraryPanels(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll("[data-book-panel]")).filter((node): node is HTMLElement => node instanceof HTMLElement);
}

export function syncLibraryPanels(root: HTMLElement, nextID?: string | null): void {
  const spines = librarySpines(root);
  const panels = libraryPanels(root);
  const activeID = String(nextID || spines[0]?.getAttribute("data-book-spine") || "").trim();

  panels.forEach((panel) => {
    panel.hidden = String(panel.getAttribute("data-book-panel") || "").trim() !== activeID;
  });
  spines.forEach((spine) => {
    spine.classList.toggle("is-active", String(spine.getAttribute("data-book-spine") || "").trim() === activeID);
  });
}

async function submitLibraryRequest(
  root: HTMLElement,
  url: string,
  body: FormData | URLSearchParams,
  successMessage: string,
  failureMessage: string,
  env: HubRuntimeEnv,
  pending?: {
    button?: HTMLButtonElement | null;
    message?: string;
  },
): Promise<void> {
  const pendingButton = pending?.button instanceof HTMLButtonElement ? pending.button : null;
  if (pendingButton) {
    setPendingButtonState(pendingButton);
  }
  if (String(pending?.message || "").trim()) {
    showLibraryFeedback(root, String(pending?.message || "").trim(), false);
  }

  try {
    const result = await postHubForm(url, body);
    if (!result.ok) {
      showLibraryFeedback(root, readHubErrorMessage(result.error, failureMessage), true);
      if (pendingButton) {
        clearPendingButtonState(pendingButton);
      }
      return;
    }
    showLibraryFeedback(root, successMessage, false);
    env.location.reload();
  } catch (_error) {
    showLibraryFeedback(root, failureMessage, true);
    if (pendingButton) {
      clearPendingButtonState(pendingButton);
    }
  }
}

function bindLibraryClicks(root: HTMLElement, env: HubRuntimeEnv, signal: AbortSignal): void {
  root.addEventListener("click", (event) => {
    const spine = nodeWithinRoot(root, closestFromEvent(event, "[data-book-spine]"));
    if (spine) {
      syncLibraryPanels(root, spine.getAttribute("data-book-spine"));
      return;
    }

    const availabilityButton = nodeWithinRoot(root, closestFromEvent(event, "[data-library-availability]"));
    if (availabilityButton instanceof HTMLButtonElement) {
      const form = availabilityButton.closest("[data-library-register]");
      const url = String(availabilityButton.getAttribute("data-library-availability-url") || "").trim();
      if (!(form instanceof HTMLFormElement) || !url) {
        return;
      }
      const body = new FormData(form);
      body.set(
        "is_available",
        availabilityButton.getAttribute("data-library-availability") === "on" ? "true" : "false",
      );
      void submitLibraryRequest(root, url, body, "Availability updated.", "Failed to update.", env);
      return;
    }

    const borrowButton = nodeWithinRoot(root, closestFromEvent(event, "[data-library-borrow]"));
    if (borrowButton instanceof HTMLButtonElement && !borrowButton.disabled) {
      const url = String(borrowButton.getAttribute("data-library-borrow") || "").trim();
      if (!url) {
        return;
      }
      void submitLibraryRequest(
        root,
        url,
        new URLSearchParams(),
        "Request sent. The owner will see it in Pending Requests.",
        "Failed to request.",
        env,
        {
          button: borrowButton,
          message: pendingMessage(borrowButton),
        },
      );
      return;
    }

    const respondButton = nodeWithinRoot(root, closestFromEvent(event, "[data-library-respond-action]"));
    if (respondButton instanceof HTMLButtonElement) {
      const form = respondButton.closest("[data-library-respond]");
      const url = form instanceof HTMLFormElement ? String(form.getAttribute("data-library-respond") || "").trim() : "";
      const action = String(respondButton.getAttribute("data-library-respond-action") || "").trim();
      if (!(form instanceof HTMLFormElement) || !url || !action) {
        return;
      }
      const body = new URLSearchParams();
      body.set("action", action);
      void submitLibraryRequest(root, url, body, "Request updated.", "Failed.", env);
    }
  }, { signal });
}

function bindLibrarySubmits(root: HTMLElement, env: HubRuntimeEnv, signal: AbortSignal): void {
  root.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !root.contains(form)) {
      return;
    }

    if (form.matches("[data-library-register]")) {
      event.preventDefault();
      const url = String(form.getAttribute("data-library-register") || "").trim();
      if (!url) {
        return;
      }
      void submitLibraryRequest(root, url, new FormData(form), "Saved.", "Failed to save.", env);
      return;
    }

    if (form.matches("[data-library-return]")) {
      event.preventDefault();
      const url = String(form.getAttribute("data-library-return") || "").trim();
      if (!url) {
        return;
      }
      void submitLibraryRequest(root, url, new FormData(form), "Loan returned.", "Failed to return.", env);
    }
  }, { signal });
}

export function mountLibraryHub(root: HTMLElement, env: HubRuntimeEnv = defaultHubRuntimeEnv()): void {
  const ctrl = new AbortController();
  syncLibraryPanels(root);
  bindLibraryClicks(root, env, ctrl.signal);
  bindLibrarySubmits(root, env, ctrl.signal);
  cleanupByRoot.set(root, () => {
    ctrl.abort();
  });
}

export function unmountLibraryHub(root: HTMLElement): void {
  cleanupByRoot.get(root)?.();
  cleanupByRoot.delete(root);
}

registerFeatureBootstrap({
  key: "participant:library-hub",
  selector: LIBRARY_HUB_SELECTOR,
  mount(root) {
    mountLibraryHub(root);
  },
  unmount(root) {
    unmountLibraryHub(root);
  },
});
